import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Mail, Lock, LockOpen, ArrowLeftRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import loginEn from './i18n/login.en.json';
import loginDe from './i18n/login.de.json';
import loginFr from './i18n/login.fr.json';
import {
  loadLoginEmailHistory,
  rememberLoginEmail,
  filterLoginEmailHistory,
} from '../utils/loginEmailHistory';
import './Login.css';

const spring = { type: 'spring', stiffness: 260, damping: 30, mass: 0.85 };

const fieldShell =
  'hawk-login-field group relative flex items-center gap-3';

const LOGIN_I18N = {
  en: loginEn,
  de: loginDe,
  fr: loginFr,
};

/** Cờ PNG nhỏ (flagcdn) — EN dùng cờ GB cho giao diện tiếng Anh */
const LANGUAGE_OPTIONS = [
  { code: 'de', label: 'Deutsch', flagSrc: 'https://flagcdn.com/w40/de.png' },
  { code: 'en', label: 'English', flagSrc: 'https://flagcdn.com/w40/gb.png' },
  { code: 'fr', label: 'Francais', flagSrc: 'https://flagcdn.com/w40/fr.png' },
];

const DECOR_BUBBLES = [
  { left: '8%', size: '16px', delay: '-2s', duration: '11s', grow: 2.05, height: '56vh', start: '-16%', drift: '14px' },
  { left: '17%', size: '9px', delay: '-7s', duration: '9.2s', grow: 2.65, height: '52vh', start: '-10%', drift: '-10px' },
  { left: '29%', size: '13px', delay: '-4s', duration: '10.2s', grow: 2.2, height: '54vh', start: '-20%', drift: '18px' },
  { left: '45%', size: '18px', delay: '-9s', duration: '12s', grow: 1.95, height: '50vh', start: '-12%', drift: '-14px' },
  { left: '57%', size: '11px', delay: '-1.5s', duration: '9s', grow: 2.35, height: '58vh', start: '-18%', drift: '12px' },
  { left: '71%', size: '15px', delay: '-5.5s', duration: '11.3s', grow: 2.1, height: '55vh', start: '-9%', drift: '-8px' },
  { left: '83%', size: '8px', delay: '-3s', duration: '8.4s', grow: 2.85, height: '62vh', start: '-22%', drift: '20px' },
  { left: '91%', size: '12px', delay: '-8s', duration: '10s', grow: 2.3, height: '57vh', start: '-14%', drift: '-12px' },
  { left: '14%', size: '20px', delay: '-4.2s', duration: '12.5s', grow: 1.9, height: '53vh', start: '-8%', drift: '10px' },
  { left: '52%', size: '10px', delay: '-3.1s', duration: '8.9s', grow: 2.7, height: '65vh', start: '-24%', drift: '-16px' },
  { left: '86%', size: '5px', delay: '-5.8s', duration: '7s', grow: 3.15, height: '70vh', start: '-26%', drift: '22px' },
];

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [language, setLanguage] = useState('en');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [brandOnLeft, setBrandOnLeft] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setEmailHistory(loadLoginEmailHistory());
  }, []);

  useEffect(() => {
    const saved = window.localStorage.getItem('hawk.login.language');
    if (saved && LOGIN_I18N[saved]) {
      setLanguage(saved);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('hawk.login.language', language);
  }, [language]);

  const suggestedEmails = useMemo(
    () => filterLoginEmailHistory(emailHistory, email),
    [emailHistory, email],
  );
  const t = LOGIN_I18N[language] || loginEn;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        rememberLoginEmail(email);
        setEmailHistory(loadLoginEmailHistory());
        const role = result.role;
        const home =
          role === 'moderator' ? '/editorial' : role === 'subscriber' ? '/newsletters' : '/dashboard';
        navigate(home, { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.06 } },
  };

  const item = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
  };

  const formInner = (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="w-full max-w-[24rem]">
      <motion.div variants={item} className="text-center">
        <h1 className="hawk-gradient-text hawk-login-gradient-heading m-0 text-4xl font-bold tracking-tight sm:text-5xl">
          Login
        </h1>
        <p className="m-0 mt-3 text-sm leading-relaxed text-slate-600">{t.newsroomSubtitle}</p>
      </motion.div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3.5">
        <motion.div variants={item}>
          <label
            className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500"
            htmlFor="login-email"
          >
            {t.emailLabel}
          </label>
          <div className="relative">
            <div className={`relative flex items-center gap-3 ${fieldShell}`}>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setShowEmailSuggestions(true)}
                onBlur={() => {
                  window.setTimeout(() => setShowEmailSuggestions(false), 180);
                }}
                placeholder={t.emailPlaceholder}
                required
                autoComplete="email"
                inputMode="email"
                className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400"
                aria-autocomplete="list"
              />
              <Mail className="h-[18px] w-[18px] shrink-0 text-slate-400 transition-colors group-focus-within:text-[var(--hawk-login-blue)]" />
            </div>
            {showEmailSuggestions && suggestedEmails.length > 0 && (
              <motion.ul
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute left-0 right-0 z-20 mt-2 max-h-48 overflow-y-auto rounded-xl bg-white py-1 text-sm"
              >
                {suggestedEmails.map((itemAddr) => (
                  <li key={itemAddr}>
                    <button
                      type="button"
                      className="w-full cursor-pointer border-0 bg-transparent px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={() => {
                        setEmail(itemAddr);
                        setShowEmailSuggestions(false);
                      }}
                    >
                      {itemAddr}
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}
          </div>
          {(error || emailHistory.length > 0) && (
            <p className={`m-0 mt-2 text-[11px] ${error ? 'text-red-600' : 'text-slate-500'}`}>
              {error ? (
                <span className="inline-flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                  {error}
                </span>
              ) : (
                t.emailHistoryHint
              )}
            </p>
          )}
        </motion.div>

        <motion.div variants={item}>
          <label
            className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500"
            htmlFor="login-password"
          >
            {t.passwordLabel}
          </label>
          <div className={`relative flex items-center gap-3 ${fieldShell}`}>
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-[var(--hawk-login-blue)] focus-visible:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <LockOpen className="h-[18px] w-[18px]" /> : <Lock className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </motion.div>

        <motion.div variants={item} className="flex items-center justify-between pt-1">
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-[var(--hawk-login-blue)]"
            />
            {t.rememberMe}
          </label>
          <button
            type="button"
            className="text-xs font-medium text-[var(--hawk-login-navy)] transition-colors hover:text-[var(--hawk-login-blue)]"
          >
            {t.forgotPassword}
          </button>
        </motion.div>

        <motion.div variants={item} className="pt-1">
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.01 }}
            whileTap={{ scale: loading ? 1 : 0.99 }}
            transition={{ type: 'spring', stiffness: 420, damping: 24 }}
            className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-[var(--hawk-login-navy)] via-[var(--hawk-login-blue)] to-[var(--hawk-login-sky)] py-2.5 text-sm font-semibold text-white transition-[filter] duration-300 ease-out hover:brightness-[1.04] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:brightness-100"
          >
            <span className="relative z-10">{loading ? t.signingIn : t.signIn}</span>
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );

  const brandInner = (
    <div className="relative z-10 flex max-w-lg flex-col justify-center px-8 py-12 md:px-14 md:py-16">
      <div className="hawk-login-bubbles" aria-hidden>
        {DECOR_BUBBLES.map((bubble, idx) => (
          <span
            key={`${bubble.left}-${idx}`}
            className="hawk-login-bubble"
            style={{
              '--bubble-left': bubble.left,
              '--bubble-size': bubble.size,
              '--bubble-delay': bubble.delay,
              '--bubble-duration': bubble.duration,
              '--bubble-grow': bubble.grow,
              '--bubble-height': bubble.height,
              '--bubble-start': bubble.start,
              '--bubble-drift': bubble.drift,
            }}
          />
        ))}
      </div>
      <div className="relative mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-800 via-blue-700 to-cyan-600 text-2xl font-bold text-white">
        H
      </div>
      <h2 className="m-0 text-3xl font-bold tracking-tight text-slate-50 md:text-4xl">
        <span>{t.brandTitleMain}</span>{' '}
        <span className="text-cyan-300">{t.brandTitleGlow}</span>{' '}
        <span>{t.brandTitleTail}</span>
      </h2>
      <p className="mt-4 max-w-md text-base leading-relaxed text-blue-100/95 md:text-lg">
        <span className="font-semibold text-cyan-300">{t.brandLine1Lead}</span> {t.brandLine1Rest}
      </p>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-blue-100/95">
        <span className="font-semibold text-cyan-300">{t.brandLine2Lead}</span> {t.brandLine2Rest}
      </p>
      <button
        type="button"
        onClick={() => setBrandOnLeft((v) => !v)}
        className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-medium text-slate-100 transition-colors hover:bg-white/16"
        title="Đổi vị trí hai cột"
      >
        <ArrowLeftRight className="h-4 w-4 text-cyan-100" aria-hidden />
        {t.swapLayout}
      </button>

      <div className="mt-6 text-xs font-medium uppercase tracking-wide text-blue-100/90">{t.languageLabel}</div>
      <div className="mt-2 flex flex-wrap items-end gap-4">
        {LANGUAGE_OPTIONS.map((opt) => (
          <button
            key={opt.code}
            type="button"
            onClick={() => setLanguage(opt.code)}
            className="group flex flex-col items-center gap-1"
            aria-label={opt.label}
            title={opt.label}
          >
            <span
              className={`relative flex h-8 w-11 items-stretch justify-center overflow-hidden rounded-md ${
                language === opt.code
                  ? 'bg-white'
                  : 'bg-white'
              }`}
            >
              <img
                src={opt.flagSrc}
                alt=""
                width={44}
                height={33}
                decoding="async"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </span>
            <span
              className={`text-[10px] font-medium uppercase tracking-wide ${
                language === opt.code ? 'text-cyan-300' : 'text-blue-100/85 group-hover:text-slate-50'
              }`}
            >
              {opt.code}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="hawk-login-cosmos relative z-0 flex min-h-[100dvh] w-full items-center justify-center px-4 py-8 text-slate-900 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="hawk-login-card relative z-10 flex w-full max-w-5xl flex-col overflow-hidden md:min-h-[min(64vh,640px)] md:h-[min(64vh,640px)]"
      >
        <div
          className={`relative z-10 flex min-h-[64vh] flex-col md:hidden ${!brandOnLeft ? 'flex-col-reverse' : ''}`}
        >
          <motion.section
            layout
            transition={spring}
            className="hawk-login-brand-panel relative flex min-h-[34vh] flex-col justify-end px-6 pb-8 pt-10"
          >
            <div className="relative">{brandInner}</div>
          </motion.section>

          <section className="hawk-login-form-panel relative flex flex-1 flex-col px-5 py-5">
            {formInner}
          </section>
        </div>

        <div className="relative hidden min-h-0 w-full md:flex md:min-h-0 md:flex-1">
          <motion.section
            className={`hawk-login-brand-panel hawk-login-brand-panel--desktop-curve absolute bottom-0 left-0 top-0 z-[3] flex w-1/2 flex-col justify-center overflow-hidden ${
              brandOnLeft
                ? 'rounded-l-[20px] rounded-r-[min(9rem,26vw)]'
                : 'rounded-l-[min(9rem,26vw)] rounded-r-[20px]'
            }`}
            animate={{ x: brandOnLeft ? 0 : '100%' }}
            transition={spring}
          >
            {brandInner}
          </motion.section>

          <motion.section
            className={`hawk-login-form-panel absolute bottom-0 left-1/2 top-0 z-[2] flex w-1/2 items-center justify-center overflow-y-auto px-7 py-6 ${
              brandOnLeft ? 'rounded-r-[20px]' : 'rounded-l-[20px]'
            }`}
            animate={{ x: brandOnLeft ? 0 : '-100%' }}
            transition={spring}
          >
            {formInner}
          </motion.section>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
